/* eslint-disable max-lines */
/**
 * Caption editor playback panel (#2233).
 *
 * Replaces the previous `caption-media-player.tsx` — the media
 * surface itself now lives in `shared/media/editor-media-surface.tsx`
 * so `caption-editor/` no longer owns a media player. This file
 * composes:
 *
 *   EditorMediaSurface
 *     + <CaptionSegmentOverlay> (caption-only chrome)
 *   + a caption-specific transport bar (slider, playback controls,
 *     linked-playback, timing shift, caption style)
 *
 * `hideControls` — used by the floating overlay path — mounts only
 * the surface.
 */

import { useCallback } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Link2,
  Palette,
  Pause,
  Play,
  Redo2,
  Undo2,
  Unlink2
} from 'lucide-react';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';
import CaptionMediaSlider from '@components/editors/caption-editor/body-right/caption-media-slider.tsx';
import { CaptionStyleModal } from '@components/editors/caption-editor/body-right/caption-style-modal.tsx';
import TimingShiftControl from '@components/editors/caption-editor/body/time-shift-control.tsx';
import { CaptionSegmentOverlay } from '@components/editors/caption-editor/body/caption-segment-overlay.tsx';
import { EditorMediaSurface } from '@components/editors/shared/media/editor-media-surface.tsx';
import { formatTime } from '@utils/audio.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { playbackRates } from '@utils/constants/playback-rates.ts';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';
import { useState } from 'react';

interface Props {
  /** Surface-only rendering — used inside the floating video overlay. */
  hideControls?: boolean;
}

export function CaptionPlayback({ hideControls = false }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { vars, cls } = useThemeVars();
  const {
    play,
    pause,
    currentTime,
    isPlaying,
    rewind,
    forward,
    playbackRate,
    setPlaybackRate,
    linkedPlayback,
    toggleLinkedPlayback
  } = useAudio();
  const { session, handleMediaElementReady } = useCaption();

  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false);
  const [isTimingOffsetOpen, setIsTimingOffsetOpen] = useState(false);

  const hasMedia = !!session?.media_url;

  const handleReady = useCallback(
    (wave?: string | null) => handleMediaElementReady(wave ?? undefined),
    [handleMediaElementReady]
  );

  return (
    <div
      className={cn(
        'h-full flex flex-col overflow-y-auto rounded',
        cls.pageBg,
        cls.text
      )}
      style={vars}
    >
      <div
        className={cn(
          'relative w-full aspect-video flex-shrink-0 overflow-hidden',
          'max-h-[calc(100vh-306px)] lg:max-h-[calc(100vh-336px)]'
        )}
      >
        <EditorMediaSurface
          mediaUrl={session?.media_url}
          mediaType={session?.media_type}
          waveInfoUrl={session?.wave_info_url}
          sessionId={session?.session_id ?? null}
          session={session}
          onReady={handleReady}
          aspectClass="aspect-video"
          overlay={<CaptionSegmentOverlay hideControls={hideControls} />}
        />
      </div>

      {!hideControls && (
        <div className="flex flex-col flex-shrink-0">
          <div
            className={cn(
              'w-full px-1 py-2 border-r border-l',
              cls.cardBg,
              cls.border,
              !hasMedia && 'opacity-50 pointer-events-none'
            )}
          >
            <CaptionMediaSlider />
          </div>

          <div
            className={cn(
              'border-x border-b border-t overflow-x-auto rounded-b-2xl flex-shrink-0 relative z-10 h-14 flex align-middle',
              cls.cardBg,
              cls.border,
              !hasMedia && 'opacity-50 pointer-events-none'
            )}
          >
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0.2) 80%, transparent)'
              }}
            />
            <div className="flex items-center justify-between p-2 w-full">
              <div className="flex items-center justify-center gap-2 flex-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('duration')}
                  </span>
                  <div
                    className="font-mono text-xs tabular-nums"
                    style={{ color: getColour('normalText') }}
                  >
                    <span>{formatTime(currentTime)}</span>
                    <span className="mx-0.5 opacity-40">/</span>
                    <span className="opacity-60">
                      {formatTime(session?.duration || 0)}
                    </span>
                  </div>
                </div>

                <div
                  className="hidden sm:block w-px h-4 opacity-20"
                  style={{ backgroundColor: getColour('normalText') }}
                />

                <Tooltip label={t('timing-offset')}>
                  <button
                    onClick={() => setIsTimingOffsetOpen(v => !v)}
                    className="flex items-center gap-1.5 pr-1 py-1 rounded-2xl transition-all hover:bg-black/5 dark:hover:bg-white/5 active:scale-95"
                    style={{
                      color: isTimingOffsetOpen
                        ? getColour('activeText')
                        : getColour('icon')
                    }}
                  >
                    <Clock size={14} aria-hidden="true" />
                  </button>
                </Tooltip>
              </div>

              <div
                className="flex items-center justify-center gap-1 px-2 border-x"
                style={{ borderColor: getColour('border') }}
              >
                <Tooltip
                  label={shortcutLabel(t('rewind-5-seconds'), 'Ctrl+← / F7')}
                >
                  <button
                    onClick={() => rewind()}
                    className="h-7 w-7 rounded-full flex items-center justify-center transition-all hover:bg-black/5 dark:hover:bg-white/5 active:scale-95"
                    style={{ color: getColour('icon') }}
                    aria-label={t('rewind-5-seconds')}
                  >
                    <Undo2 size={14} aria-hidden="true" />
                  </button>
                </Tooltip>

                <Tooltip
                  label={shortcutLabel(
                    isPlaying ? t('pause') : t('play'),
                    'Space / F5'
                  )}
                >
                  <button
                    onClick={() => (isPlaying ? pause() : play())}
                    className="h-8 w-8 rounded-full flex items-center justify-center mx-0.5 transition-all active:scale-95 shadow-sm text-white"
                    style={{ backgroundColor: getColour('activeText') }}
                    aria-label={isPlaying ? t('pause') : t('play')}
                  >
                    {isPlaying ? (
                      <Pause size={14} aria-hidden="true" />
                    ) : (
                      <Play size={14} className="ml-0.5" aria-hidden="true" />
                    )}
                  </button>
                </Tooltip>

                <Tooltip
                  label={shortcutLabel(t('forward-5-seconds'), 'Ctrl+→ / F8')}
                >
                  <button
                    onClick={() => forward(5)}
                    className="h-7 w-7 rounded-full flex items-center justify-center transition-all hover:bg-black/5 dark:hover:bg-white/5 active:scale-95"
                    style={{ color: getColour('icon') }}
                    aria-label={t('forward-5-seconds')}
                  >
                    <Redo2 size={14} aria-hidden="true" />
                  </button>
                </Tooltip>
              </div>

              <div className="flex items-center justify-evenly gap-2 flex-1 px-2">
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('speed')}
                  </span>
                  <select
                    value={playbackRate}
                    onChange={e => setPlaybackRate(Number(e.target.value))}
                    className="h-6 px-1 rounded-2xl text-xs font-mono tabular-nums cursor-pointer border bg-transparent focus:outline-none"
                    style={{
                      color: getColour('normalText'),
                      borderColor: getColour('border')
                    }}
                    aria-label={t('speed')}
                  >
                    {playbackRates.map(r => (
                      <option key={r.value} value={r.value}>
                        {r.label}x
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  className="hidden sm:block w-px h-4 opacity-20"
                  style={{ backgroundColor: getColour('normalText') }}
                />

                <Tooltip
                  label={shortcutLabel(
                    linkedPlayback
                      ? t('linked-playback-on')
                      : t('linked-playback-off'),
                    'Ctrl+L'
                  )}
                >
                  <button
                    onClick={toggleLinkedPlayback}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-2xl transition-all hover:bg-black/5 dark:hover:bg-white/5 active:scale-95"
                    style={{
                      color: linkedPlayback
                        ? getColour('activeText')
                        : getColour('icon')
                    }}
                    aria-label={
                      linkedPlayback
                        ? t('linked-playback-on')
                        : t('linked-playback-off')
                    }
                  >
                    {linkedPlayback ? (
                      <Link2 size={14} aria-hidden="true" />
                    ) : (
                      <Unlink2 size={14} aria-hidden="true" />
                    )}
                    <span
                      className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                      style={{
                        color: linkedPlayback
                          ? getColour('activeText')
                          : getColour('inactiveText')
                      }}
                    >
                      {t('linked')}
                    </span>
                  </button>
                </Tooltip>

                <div
                  className="hidden sm:block w-px h-4 opacity-20"
                  style={{ backgroundColor: getColour('normalText') }}
                />

                <Tooltip label={t('caption-styling')}>
                  <button
                    onClick={() => setIsStyleModalOpen(true)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-2xl transition-all hover:bg-black/5 dark:hover:bg-white/10 active:scale-95"
                    style={{ color: getColour('icon') }}
                    aria-label={t('caption-styling')}
                  >
                    <Palette size={14} aria-hidden="true" />
                    <span
                      className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('style')}
                    </span>
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}

      <TimingShiftControl
        isOpen={isTimingOffsetOpen}
        onClose={() => setIsTimingOffsetOpen(false)}
      />
      <CaptionStyleModal
        isOpen={isStyleModalOpen}
        onClose={() => setIsStyleModalOpen(false)}
      />
    </div>
  );
}
